import type {
  BankCatalogItem,
  BusinessDetailCatalogItem,
  CatalogServiceResponse,
  MunicipalityCatalogItem,
  NeighborhoodCatalogItem,
  ProvinceCatalogItem,
  SectorCatalogItem,
} from "@/services/assistedAffiliation/types";
import type { SelectOption } from "../types";

export const getCatalogData = <T,>(
  response: CatalogServiceResponse<T>
): T[] => {
  if (response?.status !== 200 || !Array.isArray(response.data)) return [];
  return response.data;
};

export const mapProvincesToOptions = (
  provinces: ProvinceCatalogItem[]
): SelectOption[] =>
  provinces.map((province) => ({
    label: province.province_name,
    value: province.province_id,
  }));

export const mapMunicipalitiesToOptions = (
  municipalities: MunicipalityCatalogItem[]
): SelectOption[] =>
  municipalities.map((municipality) => ({
    label: municipality.municipality_name,
    value: municipality.municipality_id,
  }));

export const mapSectorsToOptions = (
  sectors: SectorCatalogItem[]
): SelectOption[] =>
  sectors.map((sector) => ({
    label: sector.zone_name,
    value: sector.zone_id,
  }));

export const mapNeighborhoodsToOptions = (
  neighborhoods: NeighborhoodCatalogItem[]
): SelectOption[] =>
  neighborhoods.map((neighborhood) => ({
    label: neighborhood.neighborhood_name,
    value: neighborhood.neighborhood_id,
  }));

export const mapBanksToOptions = (banks: BankCatalogItem[]): SelectOption[] =>
  banks.map((bank) => ({
    label: bank.bank_name,
    value: bank.bank_id,
  }));

export const mapBusinessDetailsToOptions = (
  businessDetails: BusinessDetailCatalogItem[]
): SelectOption[] => {
  const seenValues = new Set<string>();

  return businessDetails
    .map((businessDetail) => {
      const rawValue =
        businessDetail.mcc ||
        businessDetail.detail_id ||
        businessDetail.business_detail_id ||
        businessDetail.id ||
        "";
      // Algunos ambientes entregan `mcc` como número. El estado y MUI Select trabajan con
      // strings; normalizarlo aquí evita que las validaciones llamen `.trim()` sobre un número.
      const value = String(rawValue);
      const label =
        businessDetail.detail_name_es ||
        businessDetail.detail_category ||
        businessDetail.business_detail_name ||
        businessDetail.description ||
        businessDetail.name ||
        "";

      return { label, value };
    })
    .filter((option) => {
      if (!option.label || !option.value || seenValues.has(option.value)) {
        return false;
      }

      // El backend puede repetir un MCC en varias filas. Como MUI usa el `value` para
      // identificar la opción seleccionada, conservar más de una produciría keys duplicadas
      // y una selección ambigua. Se respeta la primera descripción recibida para cada MCC.
      seenValues.add(option.value);
      return true;
    });
};
